/**
 * @file HybridTraining.tsx
 * @summary Page service
 *
 * Page descriptive d'un service proposé par la box (cours WOD, débutant, hybride, reprise du sport).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import ServicePageLayout from "@/components/ServicePageLayout";
import { Link } from "react-router-dom";

const URL = "https://crossfitchistera.fr/cours/hybrid-training";

const faq = [
  {
    "@type": "Question",
    name: "Le cours Hybrid Training prépare-t-il vraiment au Hyrox ?",
    acceptedAnswer: { "@type": "Answer", text: "Oui. La structure d'un cours Hybrid Training reproduit les blocs Hyrox : course/rameur/ski erg + stations fonctionnelles (sled, burpees, wall balls, sandbag lunges, farmer carry). C'est aussi adapté pour ATHX." },
  },
  {
    "@type": "Question",
    name: "Faut-il être compétiteur pour suivre l'Hybrid Training ?",
    acceptedAnswer: { "@type": "Answer", text: "Non. Le format est ouvert aux débutants comme aux compétiteurs : intensités, charges et distances sont adaptables. Beaucoup d'adhérents y viennent juste pour le plaisir du cardio et de l'effort long." },
  },
  {
    "@type": "Question",
    name: "Quelle différence entre WOD CrossFit et Hybrid Training ?",
    acceptedAnswer: { "@type": "Answer", text: "Le WOD CrossFit est plus court et plus intense, avec souvent une part technique (haltérophilie, gymnastique). L'Hybrid Training est orienté endurance : effort long, alternance cardio + stations, format proche d'une compétition Hyrox/ATHX." },
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Hybrid Training — Préparation Hyrox & ATHX",
    name: "Cours Hybrid Training",
    provider: { "@id": "https://crossfitchistera.fr/#localbusiness" },
    areaServed: [
      { "@type": "City", name: "Saint-Pierre-de-Chandieu" },
      { "@type": "City", name: "Lyon" },
    ],
    url: URL,
    description: "Cours Hybrid Training à Saint-Pierre-de-Chandieu : préparation directe aux compétitions Hyrox et ATHX (course, rameur, ski erg, sled, wall balls, burpees, sandbag).",
  },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

export default function HybridTraining() {
  return (
    <>
      <SEO
        title="Hybrid Training Lyon Est — Hyrox & ATHX — Chistera"
        description="Cours Hybrid Training à Saint-Pierre-de-Chandieu : préparation Hyrox et ATHX (course, rameur, sled, wall balls). Tous niveaux, coachs diplômés."
        canonical={URL}
        jsonLd={jsonLd}
      />
      <ServicePageLayout
        breadcrumbs={[{ label: "Cours" }, { label: "Hybrid Training" }]}
        h1="Hybrid Training — Préparation Hyrox & ATHX"
        intro="Le cours Hybrid Training prépare directement aux compétitions Hyrox et ATHX : alternance course/rameur/ski erg et stations fonctionnelles (sled, burpees, wall balls, sandbag lunges). Adapté aux débutants comme aux compétiteurs, encadré par des coachs diplômés. Inclus dans tous les abonnements."
      >
        <h2>Qu'est-ce que l'Hybrid Training ?</h2>
        <p>
          L'Hybrid Training est un cours collectif orienté <strong>endurance et puissance</strong>, qui combine des blocs de cardio (course à pied, rameur, ski erg, assault bike) avec des stations fonctionnelles : <strong>sled push/pull, wall balls, burpees, farmer carry, sandbag lunges</strong>. C'est la transposition fidèle du format des compétitions Hyrox et ATHX.
        </p>

        <h2>Préparer Hyrox</h2>
        <p>
          Une épreuve <strong>Hyrox</strong> = 8 × 1 km de course alternés avec 8 stations : ski erg 1000 m, sled push 50 m, sled pull 50 m, burpees broad jumps 80 m, rameur 1000 m, farmer carry 200 m, sandbag lunges 100 m, wall balls. Notre cours Hybrid Training reproduit ces enchaînements, avec une progression sur les semaines.
        </p>

        <h2>Préparer ATHX</h2>
        <p>
          <strong>ATHX</strong> est l'alternative francophone à Hyrox, avec un format similaire et des distances adaptées. La polyvalence de notre cours Hybrid Training prépare naturellement aux deux formats.
        </p>

        <h2>Pour qui ?</h2>
        <ul>
          <li><strong>Sportifs préparant une Hyrox / ATHX</strong> en simple, double ou relais.</li>
          <li><strong>Pratiquants cherchant un effort long</strong> et un travail cardio approfondi.</li>
          <li><strong>Coureurs et triathlètes</strong> qui veulent ajouter de la force fonctionnelle.</li>
          <li><strong>Adhérents WOD</strong> qui veulent varier les formats et progresser sur l'endurance — voir <Link to="/cours/wod">cours WOD</Link>.</li>
        </ul>

        <h2>Coaching individuel complémentaire</h2>
        <p>
          Pour une préparation ciblée (objectif chrono, première compétition), nous proposons aussi du coaching privé en complément des cours collectifs. Encadré par Anthony Bendahmane (Master STAPS, CrossFit Level 1) et Cédric Bastien (BPJEPS, CrossFit Level 1), tous deux anciens rugbymen.
        </p>

        <h2>Tarifs et accès</h2>
        <p>
          L'Hybrid Training est inclus dans <strong>tous les abonnements CrossFit Chistera</strong> (64 €/mois pour 2 séances, 74 €/mois pour 3, 90 €/mois illimité). Les 2 premières séances sont gratuites.
        </p>

        <h2>Questions fréquentes</h2>
        <h3>Faut-il être compétiteur ?</h3>
        <p>Non. Tout est scalé. Beaucoup viennent juste pour l'effort long.</p>
        <h3>Différence avec un WOD CrossFit ?</h3>
        <p>WOD = plus court, plus technique. Hybrid = plus long, plus cardio, format compétition Hyrox/ATHX.</p>
      </ServicePageLayout>
    </>
  );
}
